import React, { useMemo, useRef, useState } from 'react';
import { TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { EmployeeView } from '../../../../services/employees/employees.service';
import { PinPadModal } from './PinPadModal';
import { sortEmployeesForPicker } from './employee-sort.utils';
import classes from './employee-identification-gate.module.scss';

const PIN_LENGTH = 4;

type EmployeeIdentificationGateProps = {
    employees: EmployeeView[];
    isLoadingEmployees: boolean;
    isIdentifying: boolean;
    error: string | null;
    tenantPhotoUrl?: string | null;
    onIdentify: (employeeId: number, pin: string) => Promise<boolean>;
    onRefresh: () => void | Promise<void>;
    onClearError: () => void;
    onBeforeSelectEmployee?: (employee: EmployeeView) => Promise<boolean>;
};

// Architectural summary:
// Hebrew RTL employee picker that opens the reusable `PinPadModal` once the
// operator selects an employee. The picker stays mounted underneath the
// modal so closing the modal returns the operator to the same scrolled
// position with no flash.
// Touches:
//   - PinPadModal (PIN entry UX, auto-submit, error reset).
//   - employees.service.ts (via `onIdentify` in PosShellPage.handleIdentify).
// Breaking change risk: Removing the modal in favour of a separate route
// loses the "stays in context" behaviour the design relies on.
export const EmployeeIdentificationGate: React.FC<EmployeeIdentificationGateProps> = ({
    employees,
    isLoadingEmployees,
    isIdentifying,
    error: _error,
    tenantPhotoUrl,
    onIdentify,
    onRefresh,
    onClearError,
    onBeforeSelectEmployee,
}) => {
    const [search, setSearch] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeView | null>(null);
    const [failedPhotoUrls, setFailedPhotoUrls] = useState<Set<string>>(() => new Set());
    const isPhotoRefreshInFlight = useRef(false);

    // Architectural summary: Sorts (Latin, Hebrew, digits/symbols) then filters by fullName.
    const filteredEmployees = useMemo(() => {
        const sorted = sortEmployeesForPicker(employees);
        const normalized = search.trim().toLowerCase();
        if (!normalized) {
            return sorted;
        }
        return sorted.filter((employee) =>
            employee.fullName.toLowerCase().includes(normalized),
        );
    }, [employees, search]);

    const initialOf = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

    // Signed S3 URLs expire while a POS remains open. Fall back to initials for the failed URL,
    // then fetch the employee list once so every visible employee receives a fresh signed URL.
    const handlePhotoError = (photoUrl: string) => {
        setFailedPhotoUrls((previous) => new Set(previous).add(photoUrl));
        if (isPhotoRefreshInFlight.current) {
            return;
        }
        isPhotoRefreshInFlight.current = true;
        void Promise.resolve(onRefresh()).finally(() => {
            isPhotoRefreshInFlight.current = false;
        });
    };

    // Architectural summary: Closes the PIN modal and clears any prior error so the next attempt starts clean.
    const closePinModal = () => {
        setSelectedEmployee(null);
        onClearError();
    };

    // Architectural summary: Submits the entered PIN to the parent identifier; returns true on success so PinPadModal does not clear the PIN buffer (parent will unmount the gate on success anyway).
    const submitPin = async (pin: string): Promise<boolean> => {
        if (!selectedEmployee) {
            return false;
        }
        const ok = await onIdentify(selectedEmployee.id, pin);
        if (!ok) {
            setSelectedEmployee(null);
        }
        return ok;
    };

    return (
        <div className={classes.container} data-testid="employee-identification-gate">
            <div className={classes.card}>
                {tenantPhotoUrl && (
                    <div className={classes.tenantLogo}>
                        <img src={tenantPhotoUrl} alt="לוגו החברה" data-testid="employee-identification-tenant-logo" />
                    </div>
                )}
                <div className={classes.header}>
                    <span className={classes.headerSpacer} aria-hidden="true" />
                    <h2 className={classes.headerTitle}>בחר עובד להתחברות</h2>
                    <span className={classes.headerSpacer} aria-hidden="true" />
                </div>

                <div className={classes.searchRow}>
                    <div className={classes.search}>
                        <TextInput
                            data-testid="employee-identification-search"
                            value={search}
                            onChange={(event) => setSearch(event.currentTarget.value)}
                            placeholder="חיפוש עובד..."
                            size="md"
                            radius="md"
                            leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
                        />
                    </div>
                    <UnstyledButton
                        type="button"
                        data-testid="employee-identification-refresh"
                        className={classes.refreshButton}
                        onClick={onRefresh}
                        disabled={isLoadingEmployees}
                        aria-label="רענן"
                    >
                        <FontAwesomeIcon icon={faRotateRight as IconProp} />
                    </UnstyledButton>
                </div>

                <div className={classes.list}>
                    {filteredEmployees.map((employee, index) => {
                        const colored = index % 4 === 2;
                        return (
                            <UnstyledButton
                                key={employee.id}
                                type="button"
                                data-testid={`employee-identification-row-${employee.id}`}
                                className={classes.row}
                                onClick={async () => {
                                    onClearError();
                                    if (onBeforeSelectEmployee) {
                                        const proceed = await onBeforeSelectEmployee(employee);
                                        if (!proceed) return;
                                    }
                                    setSelectedEmployee(employee);
                                }}
                            >
                                <span className={`${classes.avatar} ${colored ? classes.avatarColored : ''}`}>
                                    {employee.photoUrl && !failedPhotoUrls.has(employee.photoUrl) ? (
                                        <img
                                            src={employee.photoUrl}
                                            alt={employee.fullName}
                                            onError={() => handlePhotoError(employee.photoUrl!)}
                                        />
                                    ) : (
                                        initialOf(employee.fullName)
                                    )}
                                </span>
                                <span className={classes.rowName}>{employee.fullName}</span>
                            </UnstyledButton>
                        );
                    })}
                    {filteredEmployees.length === 0 && (
                        <p className={classes.empty}>
                            {isLoadingEmployees ? 'טוען עובדים...' : 'לא נמצאו עובדים'}
                        </p>
                    )}
                </div>
            </div>

            <PinPadModal
                isOpen={selectedEmployee !== null}
                title="קוד עובד"
                subjectName={selectedEmployee?.fullName ?? ''}
                pinLength={PIN_LENGTH}
                isSubmitting={isIdentifying}
                error={null}
                onClose={closePinModal}
                onSubmit={submitPin}
            />
        </div>
    );
};
